/**
 * F 506 — THE GROUPED BOARD VIEW.
 *
 * A horizontal board whose columns are DATE BUCKETS, produced by the core
 * grouper (`src/core/views/groupRecords.ts`) that F 504 landed.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ ONE GROUPER, ONE BOARD.** ⇒⇒□ ⇒⇒□ ⇒⇒□
 * ⇒⇒□ ⇒⇒□ **⇒⇒□ THE UI'S `groupItems` ALREADY KNOWS HOW TO GROUP BY
 * STATUS / PRIORITY / ASSIGNEE / TAG / DUE-WEEK. THIS COMPONENT GROUPS BY
 * DATE.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A SECOND DATE GROUPER HERE WOULD
 * BE A SECOND ANSWER TO "WHICH COLUMN IS THIS CARD IN"** — and the brain
 * (which reads `groupRecords`) and the board (which would read something
 * else) would disagree. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE GROUPING IS
 * NOT IMPLEMENTED HERE. IT IS CALLED.**
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE PROPS ARE THE DISPATCH'S, VERBATIM:
 * records, groupBy, wrapContent, colorColumns, showIcons. ⇒⇒□ ⇒⇒□ ⇒⇒□
 * ⇒⇒□ ⇒⇒□ **⇒⇒□ AND THEY ARE ALL OPTIONAL EXCEPT `records`** — a board
 * with no records must still render its columns, or the surface blinks.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE THREE TOGGLES ARE PRESENTATION, NOT DATA:
 * `wrapContent` changes how a card's text wraps, `colorColumns` changes
 * whether the column headers carry their colour, and `showIcons` changes
 * whether a card shows its page icon. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ NONE OF
 * THEM CHANGES WHICH COLUMN A CARD IS IN.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
 */
import React, { useMemo } from 'react';
import type { DatabaseItem } from '../../../types/models/Database';
import {
  groupRecords,
  isDateGroupByMode,
  granularityForMode,
  type GroupGranularity,
  UNCATEGORIZED_KEY,
} from '../../../core/views/groupRecords';

export interface GroupedBoardViewProps {
  /** The records to lay out. */
  records: DatabaseItem[];
  /** The grouping mode. `created-day`, `created-month` and `date` are date modes. */
  groupBy?: string;
  /**
   * F 509 — WHICH DATE PROPERTY TO GROUP BY.
   *
   * ⇒⇒□ ⇒⇒□ **⇒⇒□ THE WHOLE POINT OF THE `'date'` MODE.** ⇒⇒□ ⇒⇒□ ⇒⇒□
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ THIS IS THE PROPERTY'S ID — "my_due_date", "ship_date" —
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE GRouper RESOLVES IT THROUGH THE SCHEMA.
   */
  groupByField?: string;
  /**
   * F 509 — HOW COARSELY TO BUCKET THE DATE.
   *
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ NOTION'S FIVE: `day` | `week` | `month` | `year` |
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `relative`.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND WHEN IT IS
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ABSENT, THE MODE'S OWN IMPLIED GRANULARITY WINS — SO A
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ VIEW SAVED BY F 506 STILL RENDERS EXACTLY AS IT DID.**
   */
  groupGranularity?: GroupGranularity;
  /**
   * F 509 — THE DATABASE'S PROPERTY DEFINITIONS.
   *
   * ⇒⇒□ ⇒⇒□ **⇒⇒□ WITHOUT IT, THE GROUPER CANNOT KNOW A PROPERTY IS A
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ DATE** — AND A USER'S OWN DATE PROPERTY WOULD LAND IN A
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ COLUMN NAMED AFTER ITS RAW VALUE.
   */
  schema?: ReadonlyArray<{ id: string; name: string; type?: string }>;
  /** Wrap a card's text instead of clamping it. */
  wrapContent?: boolean;
  /** Give each column header its group colour. */
  colorColumns?: boolean;
  /** Show a card's page icon. */
  showIcons?: boolean;
  /** Called when a card is clicked. */
  onItemClick?: (item: DatabaseItem) => void;
  /**
   * F 509 - CREATE A CARD FROM A DATE COLUMN.
   *
   * MEASURED: `GroupedBoardView` HAD NO "Add item" CONTROL AND NO
   * `onItemCreate` PROP AT ALL - so the DATE-MODE BOARD OFFERED NO WAY TO
   * ADD A RECORD, while the status board (KanbanView) did. The date board
   * is a first-class board; it must be able to receive a card.
   *
   * THE DATE IS CARRIED BY THE COLUMN: a card added under "2026-03-05"
   * GETS THAT DATE, so it lands in the column it was added to. The caller
   * decides what a date means - this component only passes the column's
   * key and the date the column stands for.
   */
  onItemCreate?: (column: { id: string; date?: string }) => void;
}

interface BoardColumn {
  id: string;
  name: string;
  color: string;
  items: DatabaseItem[];
  /**
   * F 509 - THE DATE THIS COLUMN STANDS FOR, WHEN IT IS A DATE COLUMN.
   *
   * The column id IS the bucket key (a UTC `YYYY-MM-DD` for a day bucket),
   * so a card added under a column can be GIVEN that date - which is what
   * makes "add a card to 2026-03-05" mean what it says. It is undefined
   * for a property-shaped column and for the relative windows.
   */
  date?: string;
}

/**
 * F 506 — THE COLUMN LIST.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE CORE GROUPER RETURNS `GroupColumn[]` WITH
 * `records`, BUT THE BOARD'S CARD TYPE IS `DatabaseItem`, AND THE CORE
 * GROUPER IS GENERIC OVER `GroupableRecord`. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
 * **⇒⇒□ `DatabaseItem` HAS `properties` AS AN ARRAY OF `{propertyId,
 * value}` — WHICH IS EXACTLY THE SHAPE THE CORE `readProperty` HANDLES**,
 * so the core grouper reads the live item shape without an adapter.
 */
function buildColumns(
  records: DatabaseItem[],
  groupBy: string | undefined,
  groupByField?: string,
  groupGranularity?: GroupGranularity,
  schema?: ReadonlyArray<{ id: string; name: string; type?: string }>,
): BoardColumn[] {
  if (!groupBy || groupBy === 'none') {
    return [{ id: 'all', name: 'All', color: '#94a3b8', items: records }];
  }

  // ⇒⇒⇒ A DATE MODE IS THE NEW CAPABILITY. A PROPERTY-SHAPED MODE
  // ('status', 'priority', …) IS THE EXISTING ONE, AND THE CORE GROUPER
  // DELEGATES THE MODE-SHAPED CASES BY NAME — so the SAME call covers
  // both. ⇒⇒⇒ The granularity is only meaningful for a date mode.
  //
  // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE GRANULARITY COMES FROM THE VIEW FIRST. ⇒⇒□ ⇒⇒□
  // ⇒⇒□ **⇒⇒□ THE MODE'S OWN IMPLIED GRANULARITY IS THE FALLBACK** — SO A
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ VIEW SAVED BY F 506 (`created-day`, NO `groupGranularity`)
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ STILL RENDERS AS IT DID, AND THE NEW AXIS ONLY TAKES OVER
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WHERE THE VIEW ACTUALLY SETS IT.
  const granularity =
    groupGranularity ?? granularityForMode(groupBy) ?? 'exact';

  // ⇒⇒⇒⇒⇒⇒⇒⇒ AND THE FIELD AND THE SCHEMA GO TOO. ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ THE SCHEMA IS WHAT LETS THE GROUPER KNOW A USER'S OWN
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ PROPERTY IS A DATE, AND THE FIELD IS WHICH ONE.**
  const grouped = groupRecords(
    records,
    groupBy,
    granularity,
    groupByField,
    schema,
  );

  return grouped.map((col) => ({
    id: col.id,
    name: col.name,
    color: col.color,
    items: col.records as DatabaseItem[],
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A DAY/MONTH/YEAR BUCKET'S KEY IS ITS DATE; A
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ RELATIVE WINDOW'S KEY IS A PHRASE, NOT A DATE.**
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE DATE IS CARRIED ONLY WHEN THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ KEY ACTUALLY PARSES AS ONE** — otherwise "add a card"
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ would invent a date.
    date: /^\d{4}-\d{2}(-\d{2})?$/.test(col.id) ? col.id : undefined,
  }));
}

/**
 * F 506 — THE CARD.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE CARD IS PLAIN, AND IT IS PLAIN ON PURPOSE.
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE BOARD'S JOB IS TO PROVE THE GROUPING;
 * IT IS NOT A NEW CARD DESIGN.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE EXISTING
 * KANBAN CARD HAS A COVER, A DRAG GRIP, A CONTEXT MENU AND A DATE LINE —
 * NONE OF WHICH BELONG IN A VIEW THAT IS PROVING COLUMNS.** ⇒⇒□ ⇒⇒□
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE CARD SHOWS THE TITLE, THE ICON (WHEN
 * ASKED), AND THE GROUPING KEY — NOTHING ELSE.**
 */
const GroupedBoardCard: React.FC<{
  item: DatabaseItem;
  wrapContent: boolean;
  showIcons: boolean;
  onClick?: (item: DatabaseItem) => void;
}> = ({ item, wrapContent, showIcons, onClick }) => {
  // ⇒⇒⇒⇒⇒⇒⇒⇒ THE CARD MUST NOT CRASH ON A RECORD WITH NO DATE.
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `new Date('invalid')` IS TRUTHY, AND
  // `toISOString()` ON AN INVALID DATE THROWS `RangeError: Invalid time
  // value`.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND THE UNCATEGORIZED
  // COLUMN IS PRECISELY WHERE THAT RECORD LIVES — SO THE ONE COLUMN
  // DESIGNED FOR THE BROKEN RECORD WAS THE ONE THAT CRASHED THE
  // BOARD.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ A TRUTHY CHECK IS NOT A VALID-DATE CHECK.
  const created = item.createdAt ? new Date(item.createdAt) : null;
  const hasValidCreated = created != null && !Number.isNaN(created.getTime());
  return (
    <div
      data-fv-grouped-card="1"
      data-fv-grouped-card-id={item.id}
      data-fv-grouped-card-name={item.title}
      onClick={onClick ? () => onClick(item) : undefined}
      style={{
        background: 'var(--mantine-color-body)',
        border: '1px solid var(--fc-border-secondary, var(--mantine-color-default-border))',
        borderRadius: 8,
        padding: '8px 10px',
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        minWidth: 0,
      }}
    >
      {showIcons && item.icon ? (
        <span
          data-fv-grouped-card-icon="1"
          aria-hidden="true"
          style={{ fontSize: 14, lineHeight: 1, flexShrink: 0 }}
        >
          {item.icon}
        </span>
      ) : null}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          data-fv-grouped-card-title="1"
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--mantine-color-text)',
            // ⇒⇒⇒ WRAP IS THE TOGGLE'S WHOLE JOB. Clamped is the
            // default; wrapped is the other state, and it is observable
            // as a different rendered height on a long title.
            ...(wrapContent
              ? { overflowWrap: 'anywhere' as const, whiteSpace: 'normal' as const }
              : {
                  overflow: 'hidden' as const,
                  textOverflow: 'ellipsis' as const,
                  whiteSpace: 'nowrap' as const,
                }),
          }}
        >
          {item.title}
        </div>
        {hasValidCreated && created ? (
          <div
            data-fv-grouped-card-date="1"
            style={{ fontSize: 11, color: 'var(--mantine-color-dimmed)', marginTop: 2 }}
          >
            {created.toISOString().slice(0, 10)}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * F 506 — THE BOARD.
 */
export const GroupedBoardView: React.FC<GroupedBoardViewProps> = ({
  records,
  groupBy,
  groupByField,
  groupGranularity,
  schema,
  wrapContent = false,
  colorColumns = true,
  showIcons = true,
  onItemClick,
  onItemCreate,
}) => {
  const columns = useMemo(
    () => buildColumns(records, groupBy, groupByField, groupGranularity, schema),
    [records, groupBy, groupByField, groupGranularity, schema],
  );

  const isDateMode = !!groupBy && isDateGroupByMode(groupBy);

  return (
    <div
      data-fv-grouped-board="1"
      data-fv-grouped-board-mode={groupBy ?? 'none'}
      data-fv-grouped-board-date-mode={isDateMode ? '1' : '0'}
      // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE FIELD AND THE GRANULARITY ARE DOM MARKERS,
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ BECAUSE A LIVE PROOF NEEDS THEM. ⇒⇒□ ⇒⇒□ ⇒⇒□
      // ⇒⇒□ ⇒⇒□ **⇒⇒□ THE MODE ALONE CANNOT PROVE WHICH PROPERTY OR HOW
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ COARSELY THE BOARD GROUPED BY** — AND A TEST THAT
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ONLY CHECKED THE MODE WOULD PASS ON A BOARD
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THAT GROUPED BY THE WRONG DATE.
      data-fv-grouped-board-field={groupByField ?? ''}
      data-fv-grouped-board-granularity={groupGranularity ?? ''}
      data-fv-grouped-board-wrap={wrapContent ? '1' : '0'}
      data-fv-grouped-board-color={colorColumns ? '1' : '0'}
      data-fv-grouped-board-icons={showIcons ? '1' : '0'}
      data-fv-grouped-board-columns={columns.length}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        overflowX: 'auto',
        overflowY: 'hidden',
        padding: 8,
        minHeight: 120,
      }}
    >
      {columns.map((col) => {
        // ⇒⇒⇒ THE UNCATEGORIZED COLUMN IS ALWAYS GREY. F 504's core
        // grouper gives it the grey colour, and the board keeps it — a
        // missing date is a fact about the record, and a bright colour
        // would make it look like a real group.
        const headerColor = colorColumns ? col.color : 'var(--mantine-color-dimmed)';
        return (
          <div
            key={col.id}
            data-fv-grouped-column="1"
            data-fv-grouped-column-id={col.id}
            data-fv-grouped-column-name={col.name}
            data-fv-grouped-column-count={col.items.length}
            data-fv-grouped-column-color={colorColumns ? col.color : ''}
            data-fv-grouped-column-uncategorized={col.id === UNCATEGORIZED_KEY ? '1' : '0'}
            style={{
              flex: '0 0 auto',
              minWidth: 200,
              maxWidth: 280,
              background: 'var(--fc-bg-secondary, var(--mantine-color-default))',
              border: '1px solid var(--fc-border-secondary, var(--mantine-color-default-border))',
              borderRadius: 10,
              padding: 10,
            }}
          >
            <div
              data-fv-grouped-column-header="1"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
                minWidth: 0,
              }}
            >
              <span
                data-fv-grouped-column-swatch="1"
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: headerColor,
                  flexShrink: 0,
                }}
              />
              <span
                data-fv-grouped-column-title="1"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--mantine-color-text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {col.name}
              </span>
              <span
                data-fv-grouped-column-count-label="1"
                style={{ fontSize: 11, color: 'var(--mantine-color-dimmed)', marginLeft: 'auto' }}
              >
                {col.items.length}
              </span>
            </div>
            <div
              data-fv-grouped-column-items="1"
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              {col.items.map((item) => (
                <GroupedBoardCard
                  key={item.id}
                  item={item}
                  wrapContent={wrapContent}
                  showIcons={showIcons}
                  onClick={onItemClick}
                />
              ))}
              {onItemCreate && (
                <button
                  type="button"
                  data-fv-grouped-column-add="1"
                  onClick={() => onItemCreate({ id: col.id, date: col.date })}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    width: '100%',
                    padding: '6px 8px',
                    fontSize: 12,
                    color: 'var(--mantine-color-dimmed)',
                    background: 'transparent',
                    border: '1px dashed var(--mantine-color-default-border)',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  + Add item
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GroupedBoardView;
